import React, { useMemo } from 'react';
import { ExternalLink, Globe, Radio } from 'lucide-react';
import { Modal } from '../../components/ui/Modal';
import { classNames } from '../../utils/classNames';

const resolveDeployHref = (deployment) => {
  if (deployment.deployUrl) {
    return deployment.deployUrl;
  }

  if (!deployment.domain) {
    return '';
  }

  return deployment.domain.startsWith('http') ? deployment.domain : `https://${deployment.domain}`;
};

export const ProductDeploymentHistoryModal = ({
  open,
  onClose,
  projectName,
  currentDeploymentId,
  deployments,
  statusTone,
  statusLabel,
  t,
  formatDate
}) => {
  const items = useMemo(
    () =>
      [...deployments].sort((a, b) => {
        const aTime = new Date(a.createdAt || a.updatedAt || 0).getTime();
        const bTime = new Date(b.createdAt || b.updatedAt || 0).getTime();
        return bTime - aTime;
      }),
    [deployments]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('cloudPreviousDeploymentsTitle')}
      panelClassName="max-w-xl rounded-xl"
      titleClassName="text-lg font-bold tracking-tight"
    >
      <div className="px-2 pb-4">
        {projectName ? (
          <p className="mb-5 text-sm leading-6 text-muted">
            {t('cloudPreviousDeploymentsHint', { name: projectName })}
          </p>
        ) : null}

        {items.length === 0 ? (
          <p className="rounded-lg border border-dashed border-border bg-panel/70 px-4 py-10 text-center text-sm text-muted">
            {t('cloudPreviousDeploymentsEmpty')}
          </p>
        ) : (
          <ol className="relative space-y-0 ps-0">
            {items.map((deployment, index) => {
              const href = resolveDeployHref(deployment);
              const isCurrent = deployment.id === currentDeploymentId;
              const publishedAt = deployment.builtAtUtc || deployment.updatedAt || deployment.createdAt;
              const isLast = index === items.length - 1;

              return (
                <li key={deployment.id} className="relative flex gap-3">
                  <div className="flex w-5 shrink-0 flex-col items-center">
                    <span
                      className={classNames(
                        'relative z-[1] mt-5 inline-flex h-3.5 w-3.5 rounded-full ring-4',
                        isCurrent
                          ? 'bg-accent ring-accent-soft'
                          : 'bg-border ring-panel-strong'
                      )}
                      aria-hidden
                    />
                    {isLast ? null : <span className="mt-1 w-px flex-1 bg-border" aria-hidden />}
                  </div>

                  <article
                    className={classNames(
                      'mb-3 min-w-0 flex-1 rounded-lg border px-4 py-3.5 transition',
                      isCurrent
                        ? 'border-accent/35 bg-accent-soft/70 shadow-sm'
                        : 'border-border bg-panel-strong/80'
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={classNames('rounded-full px-2.5 py-0.5 text-[11px] font-semibold', statusTone(deployment.status))}>
                        {statusLabel(deployment.status, t, { product: true })}
                      </span>
                      {isCurrent ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-0.5 text-[11px] font-semibold text-white dark:text-slate-950">
                          <Radio size={11} strokeWidth={2.4} aria-hidden />
                          {t('cloudPreviousDeploymentsCurrent')}
                        </span>
                      ) : null}
                    </div>

                    <p className="mt-3 flex items-center gap-2 text-sm font-semibold text-text">
                      <Globe size={15} className="shrink-0 text-muted" aria-hidden />
                      <span dir="ltr" className="min-w-0 truncate text-start">
                        {deployment.domain || '—'}
                      </span>
                    </p>

                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
                      <p className="text-xs font-medium text-muted">
                        {publishedAt ? formatDate(publishedAt, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
                      </p>
                      {href ? (
                        <a
                          href={href}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-panel px-3 py-1.5 text-xs font-semibold text-text transition hover:border-accent/30 hover:bg-accent-soft"
                        >
                          <ExternalLink size={13} aria-hidden />
                          {t('cloudOpenSite')}
                        </a>
                      ) : null}
                    </div>
                  </article>
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </Modal>
  );
};
